import { useParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import {
  AddInterviewParticipantRequest,
  GetInterviewResponse,
  RemoveInterviewParticipantRequest,
} from '@pkg/types/api'

import api from '@/api'

import { INTERVIEW_KEY } from './interview'

export const useAddInterviewParticipant = () => {
  const queryClient = useQueryClient()
  const interviewId = useParams().interviewId

  return useMutation({
    mutationFn: async (data: AddInterviewParticipantRequest) => {
      if (!interviewId) {
        throw new Error('Interview ID is required')
      }
      const response = await api.addInterviewParticipant(interviewId, {
        userId: data.userId,
      })
      return response.data
    },
    onError: (error, variables) => {
      console.error('Failed to add interview participant:', {
        error: error instanceof Error ? error.message : 'Unknown error',
        userId: variables.userId,
      })
    },
    onSuccess: (data) => {
      // Update the cache with the new participant from server
      queryClient.setQueryData<GetInterviewResponse['interview']>(
        [INTERVIEW_KEY, interviewId],
        (old) => {
          if (!old) return old
          return {
            ...old,
            interviewers: [...(old.interviewers || []), data.participant],
          }
        },
      )
    },
    onSettled: () => {
      // Ensure data consistency after mutation completes
      queryClient.invalidateQueries({
        queryKey: [INTERVIEW_KEY, interviewId],
      })
    },
  })
}

export const useRemoveInterviewParticipant = () => {
  const queryClient = useQueryClient()
  const interviewId = useParams().interviewId

  return useMutation({
    mutationFn: async (data: RemoveInterviewParticipantRequest) => {
      if (!interviewId) {
        throw new Error('Interview ID is required')
      }
      const response = await api.removeInterviewParticipant(interviewId, data)
      return response.data
    },
    onError: (error, variables) => {
      console.error('Failed to remove interview participant:', {
        error: error instanceof Error ? error.message : 'Unknown error',
        userId: variables.userId,
      })
    },
    onSuccess: (data) => {
      // Update the cache with the confirmed interviewers list from server
      queryClient.setQueryData<GetInterviewResponse['interview']>(
        [INTERVIEW_KEY, interviewId],
        (old) => {
          if (!old) return old
          return {
            ...old,
            interviewers: data.interviewers,
          }
        },
      )
    },
    onSettled: () => {
      // Ensure data consistency after mutation completes
      queryClient.invalidateQueries({
        queryKey: [INTERVIEW_KEY, interviewId],
      })
    },
  })
}
